import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ZoomIn } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './ProjectGallery.css';

const ProjectGallery = () => {
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState(null);
  const [hoveredProject, setHoveredProject] = useState(null);

  const projects = [
    {
      id: 1,
      titleKey: 'projects.project1Title',
      categoryKey: 'projects.project1Category',
      image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?q=80&w=1000',
      span: 'md:col-span-2 md:row-span-2'
    },
    {
      id: 2,
      titleKey: 'projects.project2Title',
      categoryKey: 'projects.project2Category',
      image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=1000',
      span: ''
    },
    {
      id: 3,
      titleKey: 'projects.project3Title',
      categoryKey: 'projects.project3Category',
      image: 'https://images.unsplash.com/photo-1519643381401-22c77e60520e?q=80&w=1000',
      span: ''
    },
    {
      id: 4,
      titleKey: 'projects.project4Title',
      categoryKey: 'projects.project4Category',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1000',
      span: 'md:col-span-2'
    },
    {
      id: 5,
      titleKey: 'projects.project5Title',
      categoryKey: 'projects.project5Category',
      image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=1000',
      span: ''
    },
    {
      id: 6,
      titleKey: 'projects.project6Title',
      categoryKey: 'projects.project6Category',
      image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=1000',
      span: ''
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.8, y: 30 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.4, 0, 0.2, 1]
      }
    }
  };

  return (
    <section className="project-gallery-section" id="projects">
      <div className="project-gallery-container">
        <motion.div 
          className="project-gallery-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="project-gallery-title">{t('projects.title')}</h2>
          <p className="project-gallery-subtitle">
            {t('projects.subtitle')}
          </p>
        </motion.div>

        <motion.div 
          className="bento-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              className={`bento-item ${project.span}`}
              variants={itemVariants}
              onMouseEnter={() => setHoveredProject(project.id)}
              onMouseLeave={() => setHoveredProject(null)}
              onClick={() => setSelectedImage(project)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="bento-image-wrapper">
                <motion.img 
                  src={project.image} 
                  alt={t(project.titleKey)} 
                  className="bento-image"
                  animate={{
                    scale: hoveredProject === project.id ? 1.15 : 1
                  }}
                  transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
                />
                <motion.div 
                  className="bento-overlay"
                  animate={{
                    opacity: hoveredProject === project.id ? 1 : 0.7
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div 
                    className="bento-content"
                    animate={{
                      y: hoveredProject === project.id ? 0 : 20,
                      opacity: hoveredProject === project.id ? 1 : 0.8
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    <span className="bento-category">{t(project.categoryKey)}</span>
                    <h3 className="bento-title">{t(project.titleKey)}</h3>
                    <motion.div
                      className="bento-zoom-icon"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{
                        scale: hoveredProject === project.id ? 1 : 0,
                        opacity: hoveredProject === project.id ? 1 : 0
                      }}
                      transition={{ duration: 0.3, delay: 0.1 }}
                    >
                      <ZoomIn size={24} />
                    </motion.div>
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <AnimatePresence>
        {selectedImage && (
          <motion.div 
            className="lightbox-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setSelectedImage(null)}
          >
            <motion.button 
              className="lightbox-close" 
              onClick={() => setSelectedImage(null)}
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, rotate: 180 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              whileHover={{ scale: 1.1, rotate: 90 }}
            >
              <X size={24} />
            </motion.button>
            <motion.div 
              className="lightbox-content" 
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.8, y: 50, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.8, y: 50, opacity: 0 }}
              transition={{ 
                duration: 0.4, 
                type: "spring",
                stiffness: 300,
                damping: 25
              }}
            >
              <motion.img 
                src={selectedImage.image} 
                alt={t(selectedImage.titleKey)} 
                className="lightbox-image"
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1, duration: 0.3 }}
              />
              <motion.div 
                className="lightbox-info"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.3 }}
              >
                <span className="lightbox-category">{t(selectedImage.categoryKey)}</span>
                <h3 className="lightbox-title">{t(selectedImage.titleKey)}</h3>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default ProjectGallery;
